import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useCms } from "../CmsContext";
import { PageHeader } from "../EditorChrome";
import { formatTime } from "../cmsUtils";
import { revisionSentence } from "./OverviewPage";

const PAGE_SIZE = 40;

function DiffModal({ revisionId, onClose, onRestored }) {
  const { api, API_BASE, refreshPublishStatus } = useCms();
  const [revision, setRevision] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await api.get(`${API_BASE}/revisions/${revisionId}`);
      if (res && res.success) setRevision(res.revision);
    })();
  }, [api, API_BASE, revisionId]);

  const restore = async (useField) => {
    if (
      !window.confirm(
        `Restore the draft to the state ${useField === "before" ? "before" : "after"} this change?`
      )
    ) {
      return;
    }
    setRestoring(true);
    const res = await api.post(`${API_BASE}/revisions/restore`, {
      revisionId,
      useField,
    });
    setRestoring(false);
    if (res && res.success) {
      toast.success("Draft restored — publish to make it live");
      refreshPublishStatus();
      onRestored();
    } else {
      toast.error(res?.error || "Restore failed");
    }
  };

  const pretty = (value) =>
    value == null ? "—" : JSON.stringify(value, null, 2);

  return (
    <div className="lw-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lw-modal__panel lw-modal__panel--wide">
        <div className="lw-modal__head">
          <h2 className="lw-modal__title">
            {revision ? revisionSentence(revision) : "Loading…"}
          </h2>
          <button type="button" className="lw-iconbtn" title="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="lw-modal__body">
          {revision && (
            <>
              <p className="lw-modal__lede">{formatTime(revision.createdTime)}</p>
              <div className="lw-diff">
                <div className="lw-diff__col">
                  <div className="lw-diff__head">
                    <span>Before</span>
                    {revision.before != null && revision.action !== "publish" && (
                      <button
                        type="button"
                        className="lw-btn lw-btn--secondary lw-btn--sm"
                        disabled={restoring}
                        onClick={() => restore("before")}
                      >
                        Restore this version
                      </button>
                    )}
                  </div>
                  <pre className="lw-diff__pre">{pretty(revision.before)}</pre>
                </div>
                <div className="lw-diff__col">
                  <div className="lw-diff__head">
                    <span>After</span>
                    {revision.after != null && revision.action !== "publish" && (
                      <button
                        type="button"
                        className="lw-btn lw-btn--secondary lw-btn--sm"
                        disabled={restoring}
                        onClick={() => restore("after")}
                      >
                        Restore this version
                      </button>
                    )}
                  </div>
                  <pre className="lw-diff__pre">{pretty(revision.after)}</pre>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ActivityPage() {
  const { api, API_BASE } = useCms();
  const [revisions, setRevisions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openRevision, setOpenRevision] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`${API_BASE}/revisions`, {
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    });
    if (res && res.success) {
      setRevisions(res.revisions || []);
      setTotal(res.total || 0);
    }
    setLoading(false);
  }, [api, API_BASE, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="lw-page">
      <PageHeader
        title="Activity"
        subtitle="Every change made through the CMS — click a change to compare versions or restore."
      />
      {loading ? (
        <p className="lw-empty">Loading…</p>
      ) : revisions.length === 0 ? (
        <p className="lw-empty">No changes recorded yet.</p>
      ) : (
        <ul className="lw-rowlist">
          {revisions.map((rev) => (
            <li key={rev._id}>
              <button type="button" className="lw-row lw-row--button" onClick={() => setOpenRevision(rev._id)}>
                <div className="lw-row__main">
                  <span className="lw-row__title">
                    <span className={`lw-dot lw-dot--${rev.action}`} />
                    {revisionSentence(rev)}
                  </span>
                </div>
                <div className="lw-row__side">
                  <span className="lw-row__meta">{formatTime(rev.createdTime)}</span>
                  <span className="lw-row__chevron">›</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="lw-pagination">
          <button
            type="button"
            className="lw-btn lw-btn--secondary lw-btn--sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Newer
          </button>
          <span className="lw-pagination__label">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="lw-btn lw-btn--secondary lw-btn--sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Older →
          </button>
        </div>
      )}

      {openRevision && (
        <DiffModal
          revisionId={openRevision}
          onClose={() => setOpenRevision(null)}
          onRestored={() => {
            setOpenRevision(null);
            load();
          }}
        />
      )}
    </div>
  );
}
